<template>
<u-plot-stacked
    ref="plot"
    v-if="plotData.length"
    :data="plotData"
    :series="plotSeries"
    :dark="$vuetify.theme.dark"
    duration
/>
</template>

<script>
import iso8601Duration from '@/services/iso8601Duration.js';
import uPlotStacked from '@/components/uPlotStacked.vue';

export default {
    props: {
        value: {type: Object, required: false},
        attrs: {type: Object, required: true},
        height: {type: Number, default: 500}
    },
    components: { uPlotStacked },
    computed: {
        formatLabel () { return (group, key) => this.$style.label.short(key); },
        plotSeries () {
            const rtn = [];
            const lcolors = {temp: "#7f0000", vib: "#800CBD", humi: "#403DCE"};
            const i18n_events = { 'false': this.$t('Off'), 'true': this.$t('On')};
            const i18n_step_types = {
                ramp_time: this.$t('Ramp Time'), ramp_rate: this.$t('Ramp Rate'),
                soak: this.$t('Soak'), jump: this.$t('Go To Step'), end: this.$t('End')
            }

            if (!this.attrs.loops)
                return [];
            this.attrs.loops.forEach((l, i) => {
                rtn.push({
                    "col": `${l.name}__set_value`,
                    "ptype": "num",
                    "label": this.$style.label.short(`${l.name}__set_value`),
                    "stroke": lcolors[l.name],
                    "width": 2,
                    "show": true,
                    "units": l.units,
                    points: {show: false}
                });
            })
            rtn.push(...[
                {
                    "col": "step",
                    "ptype": "state",
                    "label": this.$t("Step"),
                    "stroke": {},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": []
                },
                {
                    "col": "type",
                    "ptype": "state",
                    "label": this.$t("Type"),
                    "stroke": {},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": [],
                    i18n: v => i18n_step_types[v]
                },
                {
                    "col": "goto",
                    "ptype": "state",
                    "label": this.$t("Go To"),
                    "stroke": {'': 'black'},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": []
                },
                {
                    "col": "wait",
                    "ptype": "state",
                    "label": this.$t("Wait For"),
                    "stroke": {'': 'black'},
                    "points": false,
                    "width": 1,
                    "show": true,
                    "dash": []
                },
            ]);
            if (this.attrs.events) {
                this.attrs.events.forEach(e => {
                    rtn.push({
                        "col": e.name,
                        "ptype": "bool",
                        "label": this.formatLabel('output', e.name),
                        "stroke": {'false': 'black'},
                        "points": false,
                        "width": 1,
                        "show": true,
                        "dash": [],
                        i18n: v => i18n_events[v]
                    });
                });
            }
            if ('remotes' in this.attrs) {
                Object.keys(this.attrs.remotes).forEach(k => {
                    const val = this.attrs.remotes[k];
                    rtn.push({
                        "col": k,
                        "ptype": "bool", // TODO type === digital right now, should fix.
                        "label": this.formatLabel(val.group, k),
                        "stroke": {'false': 'black'},
                        "points": false,
                        "width": 1,
                        "show": true,
                        "dash": [],
                        i18n: v => v ? this.$t('On') : this.$t('Off') // TODO type === digital right now, should fix.
                    });
                })
            }
            return rtn;
        },
        plotData () {
            // TODO Optimize this, a lot of calculations are done repeatedly every iteration.
            const previousSetValue = (key, index) => {
                for (let i = index-1; i >= 0; i--) {
                    if (['ramp_time', 'ramp_rate'].includes(this.value.steps[i].type))
                        return this.value.steps[i][key].set_value;
                }
                return 0;
            };
            const lp_prog = (key, index, now, total) => {
                const start = previousSetValue(key, index)
                const end = this.value.steps[index][key].set_value;
                let rtn;
                switch (this.value.steps[index].type) {
                    case 'ramp_rate':
                    case 'ramp_time':
                        rtn = start + ((end - start) * (1 - (total - now) / total));
                        if (start > end)
                            rtn = rtn <= end ? end : rtn;
                        else
                            rtn = rtn >= end ? end : rtn;
                        break;
                    case 'soak':
                        rtn = start;
                        break;
                }
                return Number.isFinite(rtn) ? rtn : end;
            }
            const next_change = (now, index, durs, remotei) => {
                const d = durs.filter(d => (d - now) > 0);
                const rd = this.attrs.remotes ? iso8601Duration.toSeconds(this.value.remotes[index].steps[remotei].duration) : 0;
                if (rd - now > 0)
                    d.push(rd);
                return Math.min(...d);
            }
            const eventsCount = this.attrs.events ? this.attrs.events.length : 0;
            const iDt = 0;
            const iLoop = 1;
            const iStep = iLoop + this.attrs.loops.length;
            const iType = iStep + 1;
            const iJump = iType + 1;
            const iWait = iJump + 1;
            const iTs = iWait + 1;
            const iRemote = iTs + eventsCount;
            const rtn = [];
            for (let i = 0; i < 5 + this.attrs.loops.length + eventsCount; i++)
                rtn.push([]);
            if (this.attrs.remotes)
                Object.keys(this.attrs.remotes).forEach(() => rtn.push([]));
            let time = 0;
            this.value.steps.forEach((s, si) => {
                const rmt = this.attrs.remotes ? this.value.remotes[si] : null;
                if (['ramp_time', 'ramp_rate', 'soak'].includes(s.type)) {
                    let now = 0, remotei = 0;
                    const durs = [];
                    this.attrs.loops.forEach(l => {
                        let d = 0;
                        if (s.type == 'ramp_rate')
                            d = (Math.abs(previousSetValue(l.key, si)-s[l.key].set_value) * (1/s.rate) * 60);
                        else
                            d = iso8601Duration.toSeconds(s.duration);
                        durs.push(Number.isFinite(d) ? d : 0);
                    });
                    const totalTime = Math.max(...durs);
                    if (totalTime > 0) { //Invalid ramp rates break stuff, don't plot them.
                        while (now <= totalTime) {
                            rtn[iDt].push(time + now);
                            this.attrs.loops.forEach((l, li) => rtn[iLoop + li].push(lp_prog(l.key, si, now, durs[li])));
                            rtn[iStep].push(si+1);
                            rtn[iType].push(s.type);
                            rtn[iJump].push('');
                            if (s.wait_enable) {
                                const strings = Object.keys(this.attrs.waits).map(k => {
                                    const v = this.attrs.waits[k];
                                    const label = this.$style.label.short(v.name);
                                    if (v.enabled && k.startsWith('wait_d') && s[k] !== 'disabled') //digital wait.
                                        return `${label}(${s[k] === 'on' ? this.$t('On') : this.$t('Off')})`;
                                    else if (v.enabled && k.startsWith('wait_a') && s[k].enable) //analog wait.
                                        return `${label}(${this.$t(s[k].set_value)}${v.units})`;
                                });
                                rtn[iWait].push(strings.filter(i => i).join(' + '));
                            }
                            else {
                                rtn[iWait].push('');
                            }
                            if (this.attrs.events)
                                this.attrs.events.forEach((e, ei) => rtn[iTs + ei].push(s[e.key]));
                            if (this.attrs.remotes) {
                                if (now >= iso8601Duration.toSeconds(rmt.steps[remotei].duration) && remotei < (rmt.steps.length - 1))
                                    remotei++;
                                Object.keys(rmt.steps[remotei].data).forEach((rk, ri) => {
                                    rtn[iRemote + ri].push(rmt.steps[remotei].data[rk].value);
                                });
                            }
                            //console.log(next_change(now, si, durs, remotei))
                            now = next_change(now, si, durs, remotei);
                            //now++; // TODO: replace with next time something changes...
                        }
                    }
                    time += totalTime;
                }
                else if (s.type === 'jump') {
                    let mystr = `(${s.jump_step}\uD83D\uDDD8${si+1})*${s.jump_count}`;
                    let jumps = rtn[iJump];
                    for (let d = 0; d < jumps.length; d++) {
                        if (rtn[iStep][d] >= s.jump_step && rtn[iStep][d] < si + 1) {
                            jumps[d] += jumps[d] != '' ? `, ${mystr}` : mystr;
                        }
                    }
                }
            });
            return rtn;
        }
    },
    watch: {
        value: {
            deep: true,
            handler () { if (this.$refs.plot) this.$refs.plot.redraw(this.plotData); }
        }
    }
}
</script>
